import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Formik, Form, FormikHelpers } from 'formik';
import { observer, inject } from 'mobx-react';
import merge from 'lodash.merge';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';

import compose from 'helpers/compose';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import CustomPaper from 'components/Layout/Background';
import Switch from 'components/FormFields/Switch';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import ControlButtons from 'components/ControlButtons';
import Message, { MessageVariant } from 'components/Message/Message';

import { formatAmountValue } from 'helpers/helpers';
import { InvalidState } from '../Signature/Signature'; // FIXME: move to a general component
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { TransferStore } from 'stores/account/transfer/transferStore';
import { TransferData, initialValues } from 'stores/account/transfer/model';
import { RootStore } from 'stores/rootStore';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { TransferOption, AccountMode, Currency } from 'enums';

interface Props {
  rootStore: RootStore;
  accountDetailStore: AccountDetailStore;
}

/*
account.transfer.data.missing
account.transfer.amount.value.missing
account.transfer.amount.value.invalid
g_account.transfer.ltip.notAllowed
g_account.transfer.fee.claim
g_account.transfer.currency.invalid
g_account.transfer.data.error
g_account.transfer.balance.insufficient
g_account.transfer.amount.value.tooLowCzk
g_account.transfer.amount.value.tooHighCzk
g_account.transfer.amount.value.tooLowEur
g_account.transfer.amount.value.tooHighEur
g_account.transfer.pending
 */

const Transfer: React.FC<Props> = ({ rootStore, accountDetailStore }) => {
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const [store] = useState(new TransferStore(rootStore, accountDetailStore, accountId));
  const { fetchTransferData, data } = store;
  const { currency } = accountDetailStore;
  const translatedCurrency = useMemo(() => t(`currency_${currency}`), [currency, t]);

  useEffect(() => {
    fetchTransferData();
  }, [accountId, fetchTransferData]);

  const handleSubmit = (values: TransferData, formikHelpers: FormikHelpers<TransferData>) => {
    store.handleNext(values, formikHelpers);
  };

  initialValues.amount.currency = currency;

  if (![TransferOption.ENABLED, TransferOption.PENDING].includes(accountDetailStore.transferOption)) {
    return InvalidState(t(`accountDetail_transfer_option_${accountDetailStore.transferOption}`));
  }

  const checkLTIP = (accountId: string, accounts?: { accountId: string; mode: AccountMode }[]) => {
    return accounts?.find((a) => a.mode === AccountMode.LTIP && a.accountId === accountId) !== undefined;
  };

  const checkLTIPlimit = (value: string, transferAll: boolean, limitCzk: number, limitEur: number) => {
    const val = transferAll ? accountDetailStore.data.detail?.balance.value || 0 : Number(value);
    return val > (currency === Currency.EUR ? limitEur : limitCzk);
  };

  return data ? (
    <Formik initialValues={merge({}, initialValues, data)} onSubmit={handleSubmit}>
      {({ values, isSubmitting }) => {
        return (
          <Form id="transfer">
            <Container maxWidth="md">
              <CustomPaper>
                {checkLTIP(values.selectedAccount, values.options?.accountOptions) && (
                  <>
                    <Message
                      id="accountDetail_transfer_target_LTIP"
                      message={t('accountDetail_transfer_target_LTIP')}
                      variant={MessageVariant.INFO}
                      permanent
                    />

                    {checkLTIPlimit(
                      values.amount.value as unknown as string,
                      values.transferAll,
                      values.options.ltipThresholdCzk,
                      values.options.ltipThresholdEur,
                    ) && (
                      <Message
                        id="accountDetail_transfer_target_LTIPoverLimit"
                        message={t('accountDetail_transfer_target_LTIPoverLimit', {
                          amount: formatAmountValue(values.options.ltipThresholdCzk, Currency.CZK),
                        })}
                        variant={MessageVariant.INFO}
                        permanent
                      />
                    )}
                  </>
                )}
                <Grid container>
                  <GridSubtitle typographyProps={{ variant: 'h3' }}>{t('accountDetail_transfer_whereTo')}</GridSubtitle>

                  <Grid item xs={6}>
                    <Switch name="transferAll" label={t('accountDetail_transferAll')} />
                  </Grid>
                  <Grid item xs={6} />

                  <Grid item xs={12} sm={6}>
                    <FormSelectField
                      name="selectedAccount"
                      label={
                        values.options?.accountOptions.length === 0
                          ? t('accountDetail_transfer_noAccounts')
                          : t('accountDetail_transfer_toAccount')
                      }
                      options={values.options!.accountOptions.map((account) => ({
                        value: account.accountId,
                        label: account.title,
                      }))}
                      disabled={values.options?.accountOptions.length === 0}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormNumberField
                      fast={false}
                      label={t('accountDetail_transfer_amount')}
                      name="amount.value"
                      disabled={!!values.transferAll}
                      endAdornment={translatedCurrency}
                      specificError
                      tooltipContent={t('accountDetail_transfer_notExact')}
                    />
                    <TextField
                      name={`amount.currency`}
                      type="hidden"
                      style={{ visibility: 'hidden' }}
                      value={currency}
                    />
                  </Grid>
                </Grid>

                {/*!values.transferAll && !['', '0'].includes(values.amount.value as unknown as string) && (
                  <Message
                    id="accountDetail_transfer_notExact"
                    message={t('accountDetail_transfer_notExact')}
                    variant={MessageVariant.INFO}
                    permanent
                  />
                )*/}
              </CustomPaper>
            </Container>

            <ControlButtons
              isSubmitting={isSubmitting}
              hideBack={true}
              nextText={t(accountDetailStore.nextStepLabel)}
              nextDisabled={values.options?.accountOptions.length === 0}
            />
          </Form>
        );
      }}
    </Formik>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Transfer);
